import { Component, Input, Output, EventEmitter, HostListener, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationEventV2 } from '../../notification.model';
import { AuthService } from 'app/auth/auth.service';
import { environment } from 'environments/environment';
import { UtilsService } from '@dp/services/utils.service';
import { NotificationService } from '../../notification.service';
import { ProgressService } from 'app/shared/progress-container/progress.service';
import { finalize } from 'rxjs/operators';
import { UIService } from 'app/shared/ui.service';

@Component({
  selector: 'dp-event-detail-v2',
  templateUrl: './event-detail-v2.component.html',
  // reuse existing event-detail styles + we’ll add .linkWithSub there
  styleUrls: ['./event-detail-v2.component.scss'],
})
export class EventDetailV2Component {
  @Input() notificationEvent: NotificationEventV2;
  @Output() notificationEventChange = new EventEmitter<NotificationEventV2>();

  @Input() index: number;
  @Input() count: number;

  @Output() dismiss = new EventEmitter<void>();

  constructor(
    private router: Router,
    private authService: AuthService,
    private utilsService: UtilsService,
    private notificationService: NotificationService,
    private progressService: ProgressService,
    private hostElement: ElementRef,
    private uiService: UIService
  ) {}

  /**
   * Mark as read when clicking anywhere on the card.
   * You can later plug a V2 markRead API here.
   */
  @HostListener('click', ['$event'])
  onClick(e: MouseEvent) {
    e.stopPropagation();

    if (!this.notificationEvent?.isRead) {
      const progress = this.progressService.showProgress(this.hostElement);

      this.notificationService
        .markRead(this.notificationEvent.notificationIds)
        .pipe(finalize(() => this.progressService.detach(progress)))
        .subscribe(
          () => {
            this.notificationEvent.isRead = true;
            this.notificationEventChange.emit(this.notificationEvent);
          },
          (error) => {
            this.uiService.showSnackbar(error.message, null, {
              duration: 3000,
              panelClass: 'warn',
            });
          }
        );
    }
  }

  /**
   * Handle link clicks inside description:
   * - stop propagation (so card click doesn’t re-trigger)
   * - GA log (like ItemListComponent.redirect)
   * - navigate to target URL
   * - close dialog
   */
  onNavigate(navigateUrl: string, value: string, event: MouseEvent) {
    event.stopPropagation();

    if (!navigateUrl) {
      return;
    }

    // GA event (mirrors ItemListComponent.redirect)
    const gaEventName = environment.gaEvents?.names?.notificationClick;

    if (gaEventName) {
      const typeLower = this.notificationEvent.type?.toLowerCase() || '';
      const eventCategory = typeLower === 'event' ? 'Events' : typeLower === 'exception' ? 'Exceptions' : 'Others';

      const eventDetail = {
        eventCategory,
        eventAction: this.notificationEvent.title,
        eventLabel: value, // typically shipment/order/whatever text was clicked
      };

      this.authService.logEvent(gaEventName, eventDetail);
    }

    // Mark as read BEFORE navigating
    if (!this.notificationEvent?.isRead) {
      const progress = this.progressService.showProgress(this.hostElement);

      this.notificationService
        .markRead(this.notificationEvent.notificationIds)
        .pipe(finalize(() => this.progressService.detach(progress)))
        .subscribe(
          () => {
            this.notificationEvent.isRead = true;
            this.notificationEventChange.emit(this.notificationEvent);

            // Navigate AFTER API call succeeds
            this.performNavigation(navigateUrl);
          },
          (error) => {
            console.error('Mark read failed:', error);
          }
        );
    } else {
      // Already read, just navigate
      this.performNavigation(navigateUrl);
    }
  }

  /**
   * Helper method to handle navigation
   */
  private performNavigation(navigateUrl: string) {
    if (navigateUrl.startsWith('http://') || navigateUrl.startsWith('https://')) {
      window.open(navigateUrl, '_blank');
    } else {
      const [route, query] = navigateUrl.split('?');
      const params: Record<string, string> = {};

      if (query) {
        new URLSearchParams(query).forEach((value, key) => {
          params[key] = value;
        });
      }
      this.utilsService.migrateRouteHandler(route, { params });
    }

    // Close dialog after navigation
    this.dismiss.emit();
  }

  /**
   * Optional helper if you want a CSS class based on severity.
   * (High/Medium/Low etc.)
   */
  getSeverityClass(): string {
    if (!this.notificationEvent?.severity) {
      return '';
    }
    return `severity-${this.notificationEvent.severity.toLowerCase()}`;
  }
}
